import { prefersReducedMotion } from '@/utils/motion'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

/**
 * How far the answer column is rotated against the prompt column so no row
 * lines up with its own translation. A fixed rotation rather than a shuffle, so
 * the tiles do not jump between renders, and any offset other than zero or a
 * multiple of the row count leaves every row mismatched.
 */
const TARGET_COLUMN_OFFSET = 2

const MISMATCH_FLASH_MS = 450

/** Matches `--animate-pair-clear`, so the tile leaves as the motion ends. */
const CLEAR_MS = 340

/** The slide the survivors make once a matched pair is out of the column. */
const REFLOW_MS = 240

type Side = 'prompt' | 'target'

/**
 * Duolingo's "Tap the matching pairs": two columns, one tap per side. A match
 * swells, clears, and lets the tiles below slide up into the gap; a mismatch
 * shakes and resets the selection. Clearing the board finishes the exercise on
 * its own, with no CHECK press — Duolingo leaves the button disabled
 * throughout.
 */
export default function PairsExercise<Id extends string>({
  ids,
  onSolved,
  prompts,
  vocabulary,
}: {
  ids: Id[]
  onSolved: () => void
  /** Each id in the learner's own language. */
  prompts: Record<Id, string>
  vocabulary: Record<Id, string>
}) {
  /** Matched and gone from the grid. */
  const [matched, setMatched] = useState<Id[]>([])
  const [mismatched, setMismatched] = useState<Id[]>([])
  /** Matched, but still playing the clear animation and holding its space. */
  const [clearing, setClearing] = useState<Id[]>([])
  const [picked, setPicked] = useState<{ side: Side; word: Id } | undefined>(
    undefined,
  )

  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([])

  const tiles = useRef(new Map<string, HTMLButtonElement>())
  const tops = useRef(new Map<string, number>())

  const { t } = useTranslation()

  useEffect(
    () => () => {
      timeouts.current.forEach(clearTimeout)
    },
    [],
  )

  useEffect(() => {
    if (matched.length === ids.length) onSolved()
  }, [ids.length, matched.length, onSolved])

  /*
    A matched tile is removed from the column rather than left behind as an
    empty slot, so the grid shrinks as it is solved instead of turning into a
    field of holes. The survivors would otherwise jump up a row between frames,
    so each one is measured before and after and played back into place.
  */
  useLayoutEffect(() => {
    const before = tops.current
    const after = new Map<string, number>()

    tiles.current.forEach((node, key) => {
      const { top } = node.getBoundingClientRect()
      after.set(key, top)

      const was = before.get(key)
      if (was === undefined || was === top || prefersReducedMotion()) return

      node.animate(
        [
          { transform: `translateY(${was - top}px)` },
          { transform: 'translateY(0)' },
        ],
        { duration: REFLOW_MS, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
      )
    })

    tops.current = after
  })

  const after = (ms: number, run: () => void) => {
    timeouts.current.push(setTimeout(run, ms))
  }

  const handleTap = (side: Side, word: Id) => {
    if (matched.includes(word) || clearing.includes(word)) return

    // A tap during the mismatch flash cancels it instead of being swallowed.
    // Waiting out an animation to be allowed to try again reads as a dropped
    // tap, which is the one thing this exercise cannot afford.
    if (mismatched.length > 0) setMismatched([])

    // Tapping the lit tile again puts it out, so a misfire is undoable without
    // having to deliberately get a pair wrong.
    if (picked && picked.side === side && picked.word === word) {
      setPicked(undefined)
      return
    }

    if (!picked || picked.side === side) {
      setPicked({ side, word })
      return
    }

    if (picked.word === word) {
      // Green first, gone second. Hiding the tile on the tap gives the learner
      // nothing to confirm the match against.
      setClearing((previous) => [...previous, word])
      setPicked(undefined)

      after(CLEAR_MS, () => {
        setMatched((previous) => [...previous, word])
        setClearing((previous) => previous.filter((other) => other !== word))
      })

      return
    }

    setMismatched([picked.word, word])
    setPicked(undefined)

    after(MISMATCH_FLASH_MS, () => setMismatched([]))
  }

  const renderTile = (side: Side, word: Id) => {
    const isPicked = picked?.side === side && picked.word === word
    const isMismatched = mismatched.includes(word)
    const isClearing = clearing.includes(word)

    const key = `${side}-${word}`

    return (
      <button
        aria-pressed={isPicked}
        className={twMerge(
          'border-duo-line text-duo-ink rounded-2xl border-2 border-b-4 bg-white px-3 py-5 text-base font-bold transition-[transform,background-color,border-color,color] duration-150 active:translate-y-0.5 active:border-b-2',
          isPicked &&
            'border-duo-primary bg-duo-primary-soft text-duo-primary -translate-y-0.5',
          isMismatched &&
            'border-duo-red bg-duo-red-soft text-duo-red-text animate-pair-shake',
          isClearing &&
            'border-duo-green bg-duo-green-soft text-duo-green-text animate-pair-clear',
        )}
        disabled={isClearing}
        key={key}
        onClick={() => handleTap(side, word)}
        ref={(node) => {
          if (node) tiles.current.set(key, node)
          else tiles.current.delete(key)
        }}
        type="button"
      >
        {side === 'prompt' ? prompts[word] : vocabulary[word]}
      </button>
    )
  }

  const remaining = (column: Id[]) =>
    column.filter((id) => !matched.includes(id))

  return (
    <div className="flex grow flex-col">
      <h1 className="duo-heading">
        {t('pages.onboarding.v2.lesson.pairsTitle')}
      </h1>

      <div className="mt-8 grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-3">
          {remaining(ids).map((id) => renderTile('prompt', id))}
        </div>

        <div className="flex flex-col gap-3">
          {remaining([
            ...ids.slice(TARGET_COLUMN_OFFSET),
            ...ids.slice(0, TARGET_COLUMN_OFFSET),
          ]).map((id) => renderTile('target', id))}
        </div>
      </div>
    </div>
  )
}
